import { Routes } from '@/lib/routes';

export const REFERENCE_TO_VIDEO_PATH = '/reference-to-video';
export const REFERENCE_TO_VIDEO_DATE_PUBLISHED = '2026-09-10';
export const REFERENCE_TO_VIDEO_LAST_UPDATED = '2026-09-10';
export const REFERENCE_TO_VIDEO_LAST_UPDATED_LABEL = 'September 10, 2026';

export const REFERENCE_TO_VIDEO_SEO_TITLE =
  'MiniMax H3 Reference to Video | Hailuo 03 | Grok Imagine';
export const REFERENCE_TO_VIDEO_SEO_DESCRIPTION =
  'Use MiniMax H3 reference to video with Hailuo 03. Upload images, clips, and audio, pick 768P or 2K, then generate a 4–15s clip. Open the workbench to start.';
export const REFERENCE_TO_VIDEO_OG_IMAGE =
  '/text-to-video/text-to-video-portrait-performance-scene-poster.jpg';

export const REFERENCE_TO_VIDEO_KEYWORDS = [
  'MiniMax H3 reference to video',
  'Hailuo 03 reference to video',
  'Hailuo H3',
  'Ref2VA',
  'reference to video',
  'MiniMax H3',
  'Hailuo 03',
  'image video audio reference',
].join(', ');

export const REFERENCE_TO_VIDEO_H1 = 'MiniMax H3 Reference to Video';

export const REFERENCE_TO_VIDEO_QUICK_ANSWER =
  'MiniMax H3 reference to video turns identity stills, motion clips, and optional voice files into one 4–15 second Hailuo 03 clip. Hailuo 03 is the public name for MiniMax H3. Upload JPEG, PNG, or WebP images, MP4 or MOV videos, and MP3 or WAV audio, then describe how those files should work together.';

export const REFERENCE_TO_VIDEO_QUICK_ANSWER_FOLLOW =
  'This MiniMax H3 reference to video workbench is independent of xAI. It is not Grok Imagine Video and it is not xAI’s image-only reference-to-video product. You need at least one image or video. Audio cannot run alone. A 4-second 768P clip with no extra images and no billed source video starts at 130 site credits.';

export const REFERENCE_TO_VIDEO_TOOL_DETAILS = [
  {
    label: 'Model',
    value: 'MiniMax H3, also called Hailuo 03',
  },
  {
    label: 'Inputs',
    value: 'Up to 9 images, 3 videos, and 3 audio files',
  },
  {
    label: 'Output',
    value: '4–15 seconds at 768P or 2K',
  },
  {
    label: 'Minimum cost',
    value: '130 site credits at 4s 768P',
  },
] as const;

export const REFERENCE_TO_VIDEO_RELATED_TOOLS = [
  {
    title: 'Grok Image to Video',
    detail: 'Animate one start frame.',
    href: Routes.ImageToVideo,
    image: '/image-to-video/yoga-forward-fold-motion-source.jpg',
    alt: 'Yoga still prepared as a start frame for Grok Image to Video',
  },
  {
    title: 'Grok Video Generator',
    detail: 'Write a scene with no files.',
    href: Routes.TextToVideo,
    image: '/text-to-video/text-to-video-portrait-performance-scene-poster.jpg',
    alt: 'Portrait performance scene from the Grok Video Generator',
  },
  {
    title: 'Grok Image Generator',
    detail: 'Create identity stills first.',
    href: Routes.ImageGenerator,
    image: '/image-generator/grok-imagine-demo-poster.jpg',
    alt: 'Grok Imagine image generation example poster',
  },
  {
    title: 'AI Video Editor',
    detail: 'Change one finished clip.',
    href: Routes.AiVideoEditor,
    image: '/video-examples/example-replace-character-poster.jpg',
    alt: 'Character replacement example from the AI Video Editor',
  },
  {
    title: 'AI Video Extender',
    detail: 'Continue an approved ending.',
    href: Routes.VideoExtend,
    image: '/ai-video-extender/examples/racing-motion-continuation.jpg',
    alt: 'Racing scene continued with the AI Video Extender',
  },
  {
    title: 'AI Video Upscaler',
    detail: 'Enlarge a finished export.',
    href: Routes.VideoUpscale,
    image: '/video-upscale/ai-generated-video-polish-after-poster.jpg',
    alt: 'Polished video frame after AI video upscaling',
  },
] as const;

export const REFERENCE_TO_VIDEO_PROOF_VIDEO = {
  title: 'Performance-scene demo for identity and motion review',
  description:
    'Play this clip to judge subject continuity, timing, and a performance-style framing before you spend credits.',
  caption:
    'This is the same playable demo used in the workbench. Use it to plan a MiniMax H3 reference to video prompt that names Image, Video, and Audio slots. It is not a guaranteed MiniMax H3 output from the files you upload.',
  prompt:
    'Keep the performer identity, match the camera path from the motion clip, and align lip movement to the vocal track.',
  setting: 'Workbench demo · 768P or 2K · 4–15 seconds',
  videoUrl: '/text-to-video/text-to-video-portrait-performance-scene.mp4',
  poster: '/text-to-video/text-to-video-portrait-performance-scene-poster.jpg',
  alt: 'Portrait performance demo used to review MiniMax H3 reference to video identity and motion',
  uploadDate: '2026-09-10',
} as const;

export const REFERENCE_TO_VIDEO_PROOF_IMAGE = {
  title: 'Sample identity still for a reference image slot',
  description:
    'A clean identity photograph is a typical Image 1 input when you need the same person or product to stay readable.',
  caption:
    'Pair a still like this with a motion clip when you run MiniMax H3 reference to video. Name the slots in the prompt instead of restyling the subject in the same sentence.',
  image: '/image-to-video/yoga-forward-fold-motion-source.jpg',
  alt: 'Yoga pose still used as a MiniMax H3 reference to video identity example',
} as const;

export const REFERENCE_TO_VIDEO_FEATURES = [
  {
    title: 'Mix images, videos, and audio in one job',
    body: 'Add up to 9 JPEG, PNG, or WebP stills, 3 MP4 or MOV clips, and 3 MP3 or WAV tracks, with 12 files total. MiniMax H3 reference to video can lock a face from a still, borrow camera movement from a clip, and follow a vocal take when the prompt names those slots.',
  },
  {
    title: 'Name each file role in the prompt',
    body: 'The 7,000-character prompt is the assignment list, not a second story. In MiniMax H3 reference to video, write which image holds identity, which video supplies camera or gesture, and which audio carries voice.',
  },
  {
    title: 'Draft at 768P, then review at 2K',
    body: 'Use 768P to draft a MiniMax H3 reference to video shot, then switch to 2K when faces, logos, or product edges need a larger frame. Adaptive plus 21:9, 16:9, 4:3, 1:1, 3:4, and 9:16 cover social and cinematic delivery. The credit estimate updates before you generate.',
  },
  {
    title: 'Keep the result in History and continue',
    body: 'Completed clips save to History with the prompt and resolution. From there you can edit one visual change, extend an approved ending, or enlarge the frame. The workbench also keeps a local draft so login and checkout do not wipe uploads.',
  },
] as const;

export const REFERENCE_TO_VIDEO_STEPS = [
  {
    name: 'Write the assignment prompt',
    text: 'Describe what should stay from each file. Name Image, Video, and Audio slots in the same sentence that sets action, instead of rewriting the scene from scratch.',
  },
  {
    name: 'Add identity, motion, and optional voice',
    text: 'Upload at least one image or video. Stills must stay under 30MB, videos under 50MB and 2–15 seconds, and audio under 15MB with the same duration window. Audio cannot be the only input.',
  },
  {
    name: 'Set duration, aspect, and resolution',
    text: 'Choose 4–15 seconds, an aspect including Adaptive, and 768P or 2K. Read the credit estimate, including extra images after the fifth still and billed source-video duration.',
  },
  {
    name: 'Generate and review the full clip',
    text: 'Queue MiniMax H3 reference to video, then play the complete result. Check identity, camera path, lip sync, and any on-screen text before you download.',
  },
  {
    name: 'Save, download, or send it onward',
    text: 'Keep the MiniMax H3 reference to video clip in History, download the MP4, or continue in the editor, extender, or upscaler if the motion already passes review.',
  },
] as const;

export const REFERENCE_TO_VIDEO_SETTINGS = [
  {
    setting: 'Duration',
    options: '4–15 seconds',
    bestFor:
      'Keep short social cuts near 4–8 seconds. Use longer clips only when the motion or vocal take needs the extra time.',
  },
  {
    setting: 'Aspect ratio',
    options: 'Adaptive, 21:9, 16:9, 4:3, 1:1, 3:4, 9:16',
    bestFor:
      'Choose Adaptive when the references already agree. Pick 9:16 for stories and 16:9 for landscape delivery.',
  },
  {
    setting: 'Resolution',
    options: '768P or 2K',
    bestFor:
      'Draft MiniMax H3 reference to video at 768P, then use 2K when faces, packaging, or small type must stay sharp.',
  },
  {
    setting: 'Credits',
    options: '130 minimum at 4s 768P',
    bestFor:
      'A 6-second 768P clip is 190 credits. The same 4-second shot at 2K is 210. A 4-second source video billed with a 4-second output is 260 at 768P. A sixth image adds about 10 credits.',
  },
] as const;

export const REFERENCE_TO_VIDEO_USE_CASES = [
  {
    title: 'Keep a character while borrowing camera motion',
    body: 'A filmmaker starts with a costume still and a handheld camera clip. MiniMax H3 reference to video places the person from Image 1 into the move from Video 1. Expected output is a 6–10 second identity-stable shot, not a new random camera path.',
  },
  {
    title: 'Turn packshots into a short product move',
    body: 'A marketer uploads three product stills plus a dolly or turntable clip into MiniMax H3 reference to video. The prompt names which image is the hero pack and which video supplies the camera. Expected output is a 4–8 second 768P or 2K product beat.',
  },
  {
    title: 'Match a vocal take to a performer',
    body: 'A creator adds a portrait, a performance clip, and an MP3 or WAV vocal. MiniMax H3 reference to video keeps the face, copies the gesture, and follows Audio 1. Expected output is a lip-synced 8–15 second performance, not audio on an empty timeline.',
  },
] as const;

export const REFERENCE_TO_VIDEO_LIMITS = [
  {
    title: 'File counts and sizes',
    body: 'MiniMax H3 reference to video stays at or below 9 images, 3 videos, 3 audio files, and 12 files in total. Images max out at 30MB, videos at 50MB, and audio at 15MB. JPEG, PNG, WebP, MP4, MOV, MP3, and WAV are the accepted types.',
  },
  {
    title: 'Duration windows',
    body: 'Each video or audio reference must last 2–15 seconds. The generated clip is also 4–15 seconds. Longer source media is rejected before credits are spent.',
  },
  {
    title: 'Audio cannot run alone',
    body: 'MiniMax H3 reference to video needs at least one image or video. A vocal file without a visual reference will not queue. Pair voice with a still or a performance clip.',
  },
  {
    title: 'Identity and motion can still drift',
    body: 'Conflicting outfits, mixed lighting, or a prompt that restyles the subject can pull the face or product off-model. Review the full clip, then edit or regenerate instead of assuming one pass is final.',
  },
] as const;

export const REFERENCE_TO_VIDEO_FAQS = [
  {
    question: 'What is Hailuo 03 reference to video?',
    answer:
      'Hailuo 03 is the public name for MiniMax H3. MiniMax H3 reference to video generates a 4–15 second clip from images, videos, and optional audio. You assign each file a role in the prompt, then choose 768P or 2K before generating.',
  },
  {
    question:
      'Is this the same as Grok Image to Video or grok reference to video?',
    answer:
      'No. Grok Image to Video animates a Grok start frame. xAI’s grok reference to video product uses a different image-only workflow. This page runs MiniMax H3 reference to video with up to 9 images, 3 videos, and 3 audio files. Grok Imagine is an independent site and is not affiliated with xAI.',
  },
  {
    question: 'How many files can I upload, and how long can they be?',
    answer:
      'You can add 9 images, 3 videos, and 3 audio files, with 12 files total. Video and audio references must last 2–15 seconds. The output clip is 4–15 seconds. Images stay under 30MB, videos under 50MB, and audio under 15MB.',
  },
  {
    question: 'How are 768P and 2K credits calculated?',
    answer:
      'MiniMax H3 reference to video starts at 130 site credits for a 4-second 768P job with no extra images and no billed source video. Six seconds at 768P is 190. Four seconds at 2K is 210. Source video duration is billed with the output duration, so a 4-second input plus a 4-second output is 260 at 768P. The first five images are included; each extra image adds about 10 credits.',
  },
  {
    question: 'Can I generate from audio only?',
    answer:
      'No. Audio is optional support for voice or mix. You still need at least one image or video before MiniMax H3 reference to video will run. Add a portrait or performance clip, then name Audio 1 in the prompt.',
  },
] as const;

export const REFERENCE_TO_VIDEO_NEXT_STEPS = [
  {
    title: 'Open the clip in History',
    detail: 'Reuse the saved result without rebuilding the prompt.',
    href: Routes.History,
  },
  {
    title: 'Prepare another identity still',
    detail: 'Edit or restyle a source photograph before you add it as Image 1.',
    href: Routes.Generate,
  },
  {
    title: 'Check credit packs and plans',
    detail:
      'Compare remaining credits with 768P and 2K estimates before a longer shot.',
    href: Routes.Pricing,
  },
] as const;

export const REFERENCE_TO_VIDEO_SCHEMA_FEATURES = [
  'MiniMax H3 / Hailuo 03 reference-to-video generation',
  'Up to 9 image, 3 video, and 3 audio references',
  '4–15 second output at 768P or 2K',
  'Adaptive and common social aspect ratios',
  'Credit estimate before generation',
  'History, download, edit, extend, and upscale continuation',
] as const;

export function getReferenceToVideoSeoData() {
  return {
    path: REFERENCE_TO_VIDEO_PATH,
    seoTitle: REFERENCE_TO_VIDEO_SEO_TITLE,
    seoDescription: REFERENCE_TO_VIDEO_SEO_DESCRIPTION,
    keywords: REFERENCE_TO_VIDEO_KEYWORDS,
    ogImage: REFERENCE_TO_VIDEO_OG_IMAGE,
    datePublished: REFERENCE_TO_VIDEO_DATE_PUBLISHED,
    lastUpdated: REFERENCE_TO_VIDEO_LAST_UPDATED,
    lastUpdatedLabel: REFERENCE_TO_VIDEO_LAST_UPDATED_LABEL,
    h1: REFERENCE_TO_VIDEO_H1,
    quickAnswer: REFERENCE_TO_VIDEO_QUICK_ANSWER,
    quickAnswerFollow: REFERENCE_TO_VIDEO_QUICK_ANSWER_FOLLOW,
    toolDetails: REFERENCE_TO_VIDEO_TOOL_DETAILS,
    relatedTools: REFERENCE_TO_VIDEO_RELATED_TOOLS,
    proofVideo: REFERENCE_TO_VIDEO_PROOF_VIDEO,
    proofImage: REFERENCE_TO_VIDEO_PROOF_IMAGE,
    features: REFERENCE_TO_VIDEO_FEATURES,
    steps: REFERENCE_TO_VIDEO_STEPS,
    settings: REFERENCE_TO_VIDEO_SETTINGS,
    useCases: REFERENCE_TO_VIDEO_USE_CASES,
    limits: REFERENCE_TO_VIDEO_LIMITS,
    faqs: REFERENCE_TO_VIDEO_FAQS,
    nextSteps: REFERENCE_TO_VIDEO_NEXT_STEPS,
    schemaFeatures: REFERENCE_TO_VIDEO_SCHEMA_FEATURES,
  };
}
